import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";

import { Action } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { regionValuesArr } from "../../api/index";
import { DEFAULT_REGION_CODE } from "../../constants/index";
import { ActionTypeItems } from "../../constants/periscope-actions";
import mainStyles from "../../styles";
import { ActionByRegion } from "../../types/permissions";
import DisplayError from "../shared/display-error";
import TransferListByGroup from "./components/transfer-list-by-group";
import settingStyles from "./styles";
import { listAllActions } from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";
import { FilterTypeItem } from "components/shared/filter-type-autocomplete";

const PermissionSettingsByAction = () => {
  const classes = mainStyles();
  const settingClasses = settingStyles();

  const [actionsList, setActionsList] = useState<Action[]>([]);
  const [actionOptions, setActionOptions] = useState<FilterTypeItem[]>([]);
  const [selectedAction, setSelectedAction] = useState<Action>();
  const [actionsByRegion, setActionsByRegion] = useState<ActionByRegion>({
    us: {
      actionsList: [],
    },
    eu: {
      actionsList: [],
    },
    au: {
      actionsList: [],
    },
  });
  const [isActionsLoading, setIsActionsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const defaultRegion = useMemo(() => {
    return regionValuesArr.length === 1
      ? regionValuesArr[0]
      : DEFAULT_REGION_CODE;
  }, []);

  const handleSelectChange = (
    event: React.SyntheticEvent,
    newShown: FilterTypeItem | null,
  ) => {
    if (newShown) {
      const actionSelected = actionsList.find(
        (action) => action.actionType === newShown.Value,
      );
      setSelectedAction(actionSelected);
    }
  };
  const getActionsListReq = async () => {
    setIsActionsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listAllActions(callOptionsHeaders(regionCode))
          .then((actionsListResp) => {
            if (regionCode === defaultRegion) {
              const actionOptions = ActionTypeItems.filter((item) =>
                actionsListResp.find(
                  (action) => action.actionType === item.Value,
                ),
              );
              setActionOptions(actionOptions);
              setActionsList(actionsListResp);
            }
            const updatedActionsByRegion = { ...actionsByRegion };
            updatedActionsByRegion[regionCode].actionsList = actionsListResp;
            setActionsByRegion(updatedActionsByRegion);
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      }),
    );
    setIsActionsLoading(false);
  };

  const getSelectedAction = () => {
    if (actionOptions && actionsList.length !== 0) {
      setActionOptions(actionOptions);
    }
  };

  useEffect(() => {
    getActionsListReq();
    getSelectedAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid xs={12} minWidth={700}>
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="center"
          alignContent="space-between"
          minWidth={100}
        >
          <Grid item xs={8} margin={5}>
            <Autocomplete
              size="medium"
              id="combo-box-demo"
              limitTags={2}
              options={actionOptions}
              getOptionLabel={(option) => option.DisplayName}
              groupBy={(option) => option.Category}
              onChange={handleSelectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action"
                  data-testid="permission-action-filter"
                />
              )}
            />
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <form noValidate autoComplete="off">
          <FormControl variant="outlined" size="small" sx={classes.formControl}>
            <Grid item xs={12}>
              {isActionsLoading && (
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={3}
                  size={30}
                  sx={settingClasses.smallLoadingSpinner}
                />
              )}
            </Grid>
          </FormControl>
        </form>
      </Grid>

      <DisplayError errorMessage={errorMessage} />
      {selectedAction && (
        <TransferListByGroup
          actionsByRegion={actionsByRegion}
          selectedAction={selectedAction}
          isActionsLoading={isActionsLoading}
        />
      )}
    </Grid>
  );
};

export default PermissionSettingsByAction;
